import { useEffect, useRef } from 'react';
import type { Inspecao } from '../api/types';
import { t } from '../i18n/idioma';
import { nomeDaNotoriedade, nomeDoItem, nomeDoSlot } from '../i18n/nomes';
import { nickFillFromLabel } from '../look/nickColor';
import { CoroaVip } from './CoroaVip';
import { SeloFundador } from './SeloFundador';
import { ItemIcon } from './ItemIcon';
import { ORDEM_VISUAL, SLOT_POR_ID } from './inventario';
import { IcAr } from './icones/IconesV3';

interface Props {
  dados: Inspecao;
  /** Pinta o boneco do jogador no canvas (o motor tem o visual dele). Devolve quem desfaz. */
  bonecoDe(nome: string, canvas: HTMLCanvasElement): () => void;
}

const ICONE = 40;

/**
 * Inspecionar (Renato, 16/10): o set que o outro jogador está usando, no mesmo boneco da mochila,
 * e o que o mundo já mostra dele (profissão, notoriedade, guilda, VIP). Só leitura.
 */
export function InspecaoGump({ dados, bonecoDe }: Props) {
  const cvRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const cv = cvRef.current;
    if (!cv) return;
    return bonecoDe(dados.nome, cv);
  }, [bonecoDe, dados.nome]);

  const porSlot = new Map(dados.itens.map(i => [i.slot, i]));
  const familia = dados.conjunto.familia;
  const temConjunto = familia !== 'nenhuma' && dados.conjunto.pecas > 0;

  return (
    <div className="insp">
      <div className="insp__quem">
        <span className="insp__prof">{dados.profissao}</span>
        <span className="insp__noto" style={{ color: nickFillFromLabel(dados.notoriedade) }}>
          <i />{nomeDaNotoriedade(dados.notoriedade)}
        </span>
        {dados.selo ? <span className="insp__vip"><SeloFundador nivel={dados.selo} /></span> : null}
        {dados.vip && <span className="insp__vip" title="VIP"><CoroaVip /></span>}
        {dados.morto && <span className="insp__morto">{t('inspecao.morto')}</span>}
      </div>
      {dados.guildaTag && (
        <p className="insp__guilda" title={t('pf.guilda')}>[{dados.guildaTag}] {dados.guildaNome}</p>
      )}
      <div className="doll" role="group" aria-label={t('mochila.equipamento')}>
        <div className="doll__fig">
          <canvas ref={cvRef} className="doll__cv" width={80} height={156} aria-hidden="true" />
        </div>
        <span className="doll__chao" aria-hidden="true" />
        <span className="doll__linha" aria-hidden="true" />
        {ORDEM_VISUAL.map(id => {
          const slot = SLOT_POR_ID.get(id)!;
          const item = porSlot.get(slot.id);
          const rotulo = nomeDoSlot(slot.chave);
          const nome = item ? nomeDoItem(item.defId, item.nome) : '';
          return (
            <div
              key={slot.id}
              className={`doll-slot ${slot.cls} ${item ? 'is-worn' : 'is-empty'}`}
              title={item ? `${rotulo}: ${nome}` : rotulo}
              aria-label={item ? t('mochila.slotComItem', { slot: rotulo, item: nome }) : rotulo}
            >
              {item
                ? <ItemIcon id={item.defId} label={nome} size={ICONE} eager />
                : <ItemIcon id={slot.ghost} label="" size={ICONE} />}
            </div>
          );
        })}
      </div>
      <p className="insp__linha">
        <span title={t('inspecao.armadura')}><IcAr /> {dados.ar}</span>
        {temConjunto && (
          <span>
            {t('inspecao.conjunto', { familia: nomeFamilia(familia), n: dados.conjunto.pecas, total: dados.conjunto.pecasExigidas })}
          </span>
        )}
      </p>
    </div>
  );
}

function nomeFamilia(familia: string): string {
  const chave = `perfil.familia.${familia}`;
  const nome = t(chave);
  return nome === chave ? familia : nome;
}
